import { useMemo } from "react";
import { LenderTag, DisbursementTranche } from "../../types/deals";
import { LimitCard } from "./LimitCard";
import { Building2 } from "lucide-react";

interface LimitsTabProps {
  lenders: LenderTag[];
  disbursements: DisbursementTranche[];
}

export function LimitsTab({ lenders, disbursements = [] }: LimitsTabProps) {
  // Filter lenders with sanctions (Sanctioned or Disbursed status)
  const sanctionedLenders = useMemo(() => {
    return lenders.filter((l) => {
      const hasStatus = l.statusHistory.some(
        (e: any) => e.statusId === 8 || e.statusId === 9
      );
      const hasSanctionAmount = l.statusHistory.some(
        (e) => typeof e.sanctionAmount === "number" && e.sanctionAmount > 0
      );
      return hasStatus || hasSanctionAmount;
    });
  }, [lenders]);

  // Calculate totals
  const totals = useMemo(() => {
    let totalSanctioned = 0;
    let totalUtilized = 0;

    sanctionedLenders.forEach((l) => {
      const sanctionEntry = [...l.statusHistory]
        .reverse()
        .find((e) => typeof e.sanctionAmount === "number");
      totalSanctioned += sanctionEntry?.sanctionAmount ?? 0;

      const utilizedByLender = disbursements
        .filter((d) => d.lenderId === l.id)
        .reduce((sum, d) => sum + d.disbursedAmount, 0);
      totalUtilized += utilizedByLender;
    });

    return {
      sanctioned: totalSanctioned,
      utilized: totalUtilized,
      available: Math.max(0, totalSanctioned - totalUtilized),
    };
  }, [sanctionedLenders, disbursements]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

  if (sanctionedLenders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Building2 className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium mb-2">No Limits Available</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          Tag lenders and get sanctions to see your credit limits here. Limits appear once a lender has sanctioned the deal.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Lender Limit Cards - No Credit Line Summary (already in dashboard) */}

      {/* Lender Limit Cards */}
      <div>
        <h4 className="text-sm font-medium mb-3 text-muted-foreground">
          Lender Limits ({sanctionedLenders.length})
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sanctionedLenders.map((lender) => (
            <LimitCard
              key={lender.id}
              lender={lender}
              disbursements={disbursements}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
